/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { Input, Button, Space, Typography, Divider } from "antd";
import { CloseOutlined } from "@ant-design/icons";

const { Text } = Typography;

// Helper: concatenar segmentos con AIs SOLO si tienen valores
const concatenateSegments = (segments, type) => {
  if (type === "cb1") {
    const parts = [];
    if (segments[0]) parts.push(`(02)${segments[0]}`);
    if (segments[1]) parts.push(`(20)${segments[1]}`);
    if (segments[2]) parts.push(`(15)${segments[2]}`);
    if (segments[3]) parts.push(`(37)${segments[3]}`);
    return parts.join("");
  }

  if (type === "cb2") {
    return segments[0] ? `(10)${segments[0]}` : "";
  }
  if (type === "cb3") {
    const parts = [];
    if (segments[0]) parts.push(`(00)${segments[0]}`);
    if (segments[1]) parts.push(`(240)${segments[1]}`);
    return parts.join("");
  }
  return "";
};

// Helper: obtener código limpio (sin paréntesis)
const getCleanCode = (segments, type) => {
  return concatenateSegments(segments, type).replace(/[()]/g, "");
};

// Subcomponente: Teclado numérico
const NumericKeypad = ({
  onKeyPress,
  onConfirm,
  onClose,
  darkMode = false,
}) => {
  const bgColor = darkMode ? "#1f1f1f" : "#fafafa";
  const textColor = darkMode ? "#ffffff" : "#000000";
  const buttonBg = darkMode ? "#252525" : "#eee";
  const buttonBorder = darkMode ? "#444" : "#ddd";

  const handlePress = (key) => {
    if (key === "BACKSPACE") {
      onKeyPress({ type: "BACKSPACE" });
    } else if (key === "CLEAR") {
      onKeyPress({ type: "CLEAR" });
    } else if (key === "ENTER") {
      onConfirm?.();
    } else {
      onKeyPress({ type: "CHAR", value: key });
    }
  };

  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["0", "BACKSPACE", "CLEAR"],
  ];

  return (
    <div
      style={{
        padding: "12px",
        background: bgColor,
        borderRadius: "8px",
      }}
    >
      <Space
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <Text strong style={{ fontSize: "15px", color: textColor }}>
          {" "}
          Teclado numérico
        </Text>
        <Button
          type="text"
          icon={
            <CloseOutlined
              style={{
                fontSize: "17px",
                color: textColor,
              }}
            />
          }
          onClick={onClose}
          size="small"
          style={{ color: textColor }}
        />
      </Space>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
        }}
      >
        {keys.flat().map((key) => {
          const isEnter = key === "ENTER";
          const isSpecial = key === "CLEAR" || key === "BACKSPACE";

          return (
            <Button
              key={key}
              onClick={() => handlePress(key)}
              style={{
                height: "60px",
                fontSize: isSpecial ? "25px" : "25px",
                backgroundColor: isEnter ? "#52c41a" : buttonBg, // 👈 Adaptable
                borderColor: isEnter ? "#52c41a" : buttonBorder, // 👈 Adaptable
                color: isEnter ? "#fff" : textColor, // 👈 Adaptable
              }}
            >
              {key === "BACKSPACE"
                ? "←"
                : key === "CLEAR"
                  ? "C"
                  : key === "ENTER"
                    ? "✓"
                    : key}
            </Button>
          );
        })}
      </div>

      {onConfirm && (
        <Button
          type="primary"
          block
          onClick={onConfirm}
          style={{
            marginTop: 12,
            backgroundColor: "#52c41a",
            borderColor: "#52c41a",
          }}
        >
          Confirmar código
        </Button>
      )}
    </div>
  );
};

// Subcomponente: Input con prefijo AI fijo
const SegmentedInput = ({
  label,
  segments,
  setSegments,
  segmentLabels,
  segmentAIs,
  isActive,
  onToggleKeypad,
  onSegmentComplete,
  isAlphanumeric = false,
  type,
  darkMode,
}) => {
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(
    segments.length - 1,
  );
  const inputRefs = useRef([]);

  const handleKeypress = ({ type: actionType, value }) => {
    const newSegments = [...segments];
    if (actionType === "BACKSPACE") {
      newSegments[activeSegmentIndex] = newSegments[activeSegmentIndex].slice(
        0,
        -1,
      );
    } else if (actionType === "CLEAR") {
      newSegments[activeSegmentIndex] = "";
    } else if (actionType === "CHAR") {
      const cleanValue = isAlphanumeric
        ? value.replace(/[^0-9A-Za-z]/g, "")
        : value.replace(/[^0-9]/g, "");
      newSegments[activeSegmentIndex] += cleanValue;
    }
    setSegments(newSegments);
  };

  const handleConfirm = () => {
    const fullCode = getCleanCode(segments, type);
    onToggleKeypad();
    onSegmentComplete?.(fullCode);
  };

  useEffect(() => {
    if (isActive && isAlphanumeric && inputRefs.current[activeSegmentIndex]) {
      inputRefs.current[activeSegmentIndex]?.focus();
    }
  }, [isActive, isAlphanumeric, activeSegmentIndex]);

  return (
    <div style={{ width: "100%" }}>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Text strong>{label}</Text>
        <Button
          type="text"
          onClick={onToggleKeypad}
          style={{ padding: "4px 8px", minWidth: "auto" }}
        >
          <img
            src={darkMode ? "/icons/keyboard-white.png" : "/icons/keyboard.png"}
            alt="teclado"
            style={{ width: 30, height: 30 }}
          />
        </Button>
      </Space>
      {!isActive && (
        <Input
          value={concatenateSegments(segments, type)}
          readOnly
          placeholder="Escaneá o tipeá el código"
          size="large"
          style={{
            marginBottom: 4,
            fontFamily: "monospace",
            fontSize: "17px",
          }}
        />
      )}
      {isActive && (
        <Space
          direction="vertical"
          size={8}
          style={{ width: "100%", marginBottom: 8 }}
        >
          {segments.map((segment, idx) => (
            <div
              key={idx}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              {/* Label descriptivo */}
              <Text
                style={{
                  minWidth: 90,
                  fontSize: "13px",
                  fontWeight: activeSegmentIndex === idx ? "bold" : "normal",
                  cursor: isAlphanumeric ? "default" : "pointer",
                }}
                onClick={() => !isAlphanumeric && setActiveSegmentIndex(idx)}
              >
                {segmentLabels[idx]}:
              </Text>

              {/* Input con AI como prefijo visual NO editable */}
              <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                <span
                  style={{
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                    padding: "6px 10px",
                    borderRadius: "6px 0 0 6px",
                    fontWeight: "600",
                    fontSize: "12px",
                    border: "1px solid #e0e0e0",
                    borderRight: "none",
                    letterSpacing: "0.5px",
                    minWidth: "50px",
                    textAlign: "center",
                  }}
                >
                  {segmentAIs[idx]}
                </span>
                <Input
                  ref={(el) => (inputRefs.current[idx] = el)}
                  value={segment}
                  onChange={(e) => {
                    const newSegments = [...segments];
                    const cleanValue = isAlphanumeric
                      ? e.target.value.replace(/[^0-9A-Za-z]/g, "")
                      : e.target.value.replace(/[^0-9]/g, "");
                    newSegments[idx] = cleanValue;
                    setSegments(newSegments);
                    setActiveSegmentIndex(idx);
                  }}
                  placeholder={`Ingrese ${segmentLabels[idx].toLowerCase()}`}
                  maxLength={
                    type === "cb1"
                      ? idx === 0
                        ? 14
                        : idx === 1
                          ? 2
                          : idx === 2
                            ? 6
                            : undefined
                      : idx === 0 && type === "cb3"
                        ? 18
                        : undefined
                  }
                  style={{
                    flex: 1,
                    borderRadius: "0 4px 4px 0",
                    fontSize: "17px",
                  }}
                  size="medium"
                  readOnly={!isAlphanumeric}
                  onClick={() => !isAlphanumeric && setActiveSegmentIndex(idx)}
                  inputMode={isAlphanumeric ? "text" : "numeric"}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="characters"
                />
              </div>
            </div>
          ))}

          {!isAlphanumeric && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <NumericKeypad
                onKeyPress={handleKeypress}
                onConfirm={handleConfirm}
                onClose={onToggleKeypad}
                darkMode={darkMode}
              />
            </>
          )}

          {isAlphanumeric && (
            <>
              <Button
                type="primary"
                block
                onClick={handleConfirm}
                style={{ marginTop: 12 }}
              >
                Confirmar código
              </Button>
              <Text
                type="secondary"
                style={{
                  fontSize: "12px",
                  display: "block",
                  textAlign: "center",
                  marginTop: 8,
                }}
              >
                ⌨️ Usá el teclado de tu dispositivo
              </Text>
            </>
          )}
        </Space>
      )}
    </div>
  );
};

const ManualBarcodeInput = ({
  onConfirm,
  onCancel,
  onDataChange,
  initialCode = null, // Código crudo detectado desde afuera
  darkMode,
}) => {
  const [loading, setLoading] = useState(false);
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  // cb1: (02)GTIN(20)Variant(15)Vencimiento(37)Count
  const [cb1Segments, setCb1Segments] = useState(["", "", "", ""]);
  const cb1Config = {
    labels: ["GTIN", "Variante", "Vencimiento", "Cantidad"],
    ais: ["(02)", "(20)", "(15)", "(37)"],
    type: "cb1",
  };

  // cb2: (10)Lote
  const [cb2Segments, setCb2Segments] = useState([""]);
  const cb2Config = {
    labels: ["Lote"],
    ais: ["(10)"],
    type: "cb2",
  };

  // cb3: (00)SSCC(240)MaterialCode
  const [cb3Segments, setCb3Segments] = useState(["", ""]);
  const cb3Config = {
    labels: ["SSCC", "Material Code"],
    ais: ["(00)", "(240)"],
    type: "cb3",
  };

  const [activeKeypad, setActiveKeypad] = useState(null);
  const bufferRef = useRef("");

  const scannedCodesRef = useRef({
    cb1: "",
    cb2: "",
    cb3: "",
  });

  const scanSessionRef = useRef(0);

  const segmentConfig = {
    cb1: { ...cb1Config, setSegments: setCb1Segments, isAlphanumeric: false },
    cb2: { ...cb2Config, setSegments: setCb2Segments, isAlphanumeric: true },
    cb3: { ...cb3Config, setSegments: setCb3Segments, isAlphanumeric: false },
  };

  const onDataChangeRef = useRef(onDataChange);

  // Efecto para auto-parsear initialCode (normalizado a uppercase)
  useEffect(() => {
    if (initialCode) {
      setIsAutoDetected(true);
      const normalized = initialCode.trim().replace(/[()]/g, "").toUpperCase();

      if (normalized.startsWith("02")) {
        procesarCodigo(normalized, "cb1");
      } else if (normalized.startsWith("10")) {
        procesarCodigo(normalized, "cb2");
      } else if (normalized.startsWith("00")) {
        procesarCodigo(normalized, "cb3");
      }
    }
  }, [initialCode]);

  // Efecto para auto-confirmar si es auto-detectado y los 3 códigos están completos
  // useEffect(() => {
  //   if (isAutoDetected) {
  //     const fullCb1 = getCleanCode(cb1Segments, "cb1");
  //     const fullCb2 = getCleanCode(cb2Segments, "cb2");
  //     const fullCb3 = getCleanCode(cb3Segments, "cb3");

  //     const allFilled = fullCb1.trim() && fullCb2.trim() && fullCb3.trim();

  //     console.log("AUTO CHECK", {
  //       isAutoDetected,
  //       cb1: fullCb1,
  //       cb2: fullCb2,
  //       cb3: fullCb3,
  //       allFilled,
  //     });

  //     if (allFilled && !loading) {
  //       const timer = setTimeout(() => {
  //         handleConfirm();
  //       }, 300);
  //       return () => clearTimeout(timer);
  //     }
  //   }
  // }, [cb1Segments, cb2Segments, cb3Segments, isAutoDetected, loading]);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    const fullCb1 = getCleanCode(cb1Segments, "cb1");
    const fullCb2 = getCleanCode(cb2Segments, "cb2");
    const fullCb3 = getCleanCode(cb3Segments, "cb3");

    const hasData =
      cb1Segments.some((s) => s.trim()) ||
      cb2Segments.some((s) => s.trim()) ||
      cb3Segments.some((s) => s.trim());

    onDataChangeRef.current?.({
      hasData,
      values: { cb1: fullCb1, cb2: fullCb2, cb3: fullCb3 },
    });
  }, [cb1Segments, cb2Segments, cb3Segments]);

  // const procesarCodigo = (code, type) => {
  //   const clean = code.trim().replace(/[()]/g, "").toUpperCase();

  //   if (type === "cb1") {
  //     const tryParseCb1 = (value, gtinLength) => {
  //       let pos = 0;

  //       if (!value.startsWith("02")) return null;
  //       pos += 2;

  //       const gtin = value.slice(pos, pos + gtinLength);
  //       if (gtin.length !== gtinLength || !/^\d+$/.test(gtin)) return null;
  //       pos += gtinLength;

  //       // AI (20) opcional
  //       if (value.slice(pos, pos + 2) === "20") {
  //         pos += 2;
  //       }

  //       const variant = value.slice(pos, pos + 2);
  //       if (variant.length !== 2 || !/^\d+$/.test(variant)) return null;
  //       pos += 2;

  //       // Algunos códigos traen 00 antes del AI 15
  //       if (value.slice(pos, pos + 2) === "00") {
  //         pos += 2;
  //       }

  //       if (value.slice(pos, pos + 2) !== "15") return null;
  //       pos += 2;

  //       const vencimiento = value.slice(pos, pos + 6);
  //       if (vencimiento.length !== 6 || !/^\d+$/.test(vencimiento)) return null;
  //       pos += 6;

  //       if (value.slice(pos, pos + 2) !== "37") return null;
  //       pos += 2;

  //       const count = value.slice(pos);
  //       if (!count.length || !/^\d+$/.test(count)) return null;

  //       return [gtin, variant, vencimiento, count];
  //     };

  //     const parsed = tryParseCb1(clean, 14) || tryParseCb1(clean, 13);

  //     if (parsed) {
  //       setCb1Segments(parsed);
  //     } else {
  //       setCb1Segments(["", "", "", ""]);
  //     }

  //     return;
  //   }

  //   if (type === "cb2") {
  //     const idx10 = clean.indexOf("10");
  //     const lote = idx10 !== -1 ? clean.slice(idx10 + 2) : clean;
  //     setCb2Segments([lote]);
  //     return;
  //   }

  //   if (type === "cb3") {
  //     let pos = 0;
  //     if (clean.startsWith("00")) pos = 2;
  //     // 18 dígitos fijos
  //     const sscc = clean.slice(pos, pos + 18);
  //     pos += 18;

  //     if (clean.slice(pos, pos + 3) === "240") pos += 3;

  //     const materialCode = clean.slice(pos);

  //     setCb3Segments([sscc, materialCode]);
  //     return;
  //   }
  // };

  const procesarCodigo = (code, type) => {
    const clean = code.trim().replace(/[()]/g, "").toUpperCase();

    if (!clean) return;

    if (type === "cb1") {
      const tryParseCb1 = (value, gtinLength) => {
        let pos = 0;

        if (!value.startsWith("02")) return null;
        pos += 2;

        const gtin = value.slice(pos, pos + gtinLength);

        if (gtin.length !== gtinLength || !/^\d+$/.test(gtin)) {
          return null;
        }

        pos += gtinLength;

        // AI (20) opcional
        if (value.slice(pos, pos + 2) === "20") {
          pos += 2;
        }

        const variant = value.slice(pos, pos + 2);

        if (variant.length !== 2 || !/^\d+$/.test(variant)) {
          return null;
        }

        pos += 2;

        // Algunos códigos traen 00 antes del AI 15
        if (value.slice(pos, pos + 2) === "00") {
          pos += 2;
        }

        if (value.slice(pos, pos + 2) !== "15") {
          return null;
        }

        pos += 2;

        const vencimiento = value.slice(pos, pos + 6);

        if (vencimiento.length !== 6 || !/^\d+$/.test(vencimiento)) {
          return null;
        }

        pos += 6;

        if (value.slice(pos, pos + 2) !== "37") {
          return null;
        }

        pos += 2;

        const count = value.slice(pos);

        if (!count.length || !/^\d+$/.test(count)) {
          return null;
        }

        return [gtin, variant, vencimiento, count];
      };

      const parsed = tryParseCb1(clean, 14) || tryParseCb1(clean, 13);

      if (!parsed) {
        console.warn("CB1 inválido:", clean);
        return;
      }

      const fullCode = getCleanCode(parsed, "cb1");

      // Fuente de verdad
      scannedCodesRef.current.cb1 = fullCode;

      // UI
      setCb1Segments(parsed);

      return;
    }

    if (type === "cb2") {
      const idx10 = clean.indexOf("10");

      const lote = idx10 !== -1 ? clean.slice(idx10 + 2) : clean;

      if (!lote) {
        console.warn("CB2 inválido:", clean);
        return;
      }

      const fullCode = `(10)${lote}`;

      // Fuente de verdad
      scannedCodesRef.current.cb2 = fullCode;

      // UI
      setCb2Segments([lote]);

      return;
    }

    if (type === "cb3") {
      let pos = 0;

      if (clean.startsWith("00")) {
        pos = 2;
      }

      // SSCC = 18 dígitos
      const sscc = clean.slice(pos, pos + 18);

      if (sscc.length !== 18 || !/^\d+$/.test(sscc)) {
        console.warn("CB3 inválido:", clean);
        return;
      }

      pos += 18;

      if (clean.slice(pos, pos + 3) === "240") {
        pos += 3;
      }

      const materialCode = clean.slice(pos);

      if (!materialCode) {
        console.warn("CB3 inválido:", clean);
        return;
      }

      const parsed = [sscc, materialCode];

      const fullCode = getCleanCode(parsed, "cb3");

      // Fuente de verdad
      scannedCodesRef.current.cb3 = fullCode;

      // UI
      setCb3Segments(parsed);

      return;
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeKeypad) return;
      e.stopPropagation();

      if (e.key === "Enter") {
        const code = bufferRef.current.trim();

        if (code) {
          let type = null;

          if (code.startsWith("02")) {
            type = "cb1";
          } else if (code.startsWith("10")) {
            type = "cb2";
          } else if (code.startsWith("00")) {
            type = "cb3";
          }

          if (type) {
            procesarCodigo(code, type);
            // Esperamos al siguiente tick para que la lectura
            // haya quedado registrada en el flujo de escaneo.
            setTimeout(() => {
              tryAutoConfirm();
            }, 0);
          }
        }

        bufferRef.current = "";

        return;
      }

      if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeKeypad]);

  const tryAutoConfirm = () => {
    if (loading) return;

    const { cb1, cb2, cb3 } = scannedCodesRef.current;

    // Todavía falta algún código
    if (!cb1 || !cb2 || !cb3) {
      return;
    }

    // Validación final
    const valid =
      cb1.replace(/[()]/g, "").startsWith("02") &&
      cb2.replace(/[()]/g, "").startsWith("10") &&
      cb3.replace(/[()]/g, "").startsWith("00");

    if (!valid) {
      console.warn(
        "No se confirma: códigos inválidos",
        scannedCodesRef.current,
      );

      return;
    }

    // Snapshot INMUTABLE de esta lectura
    const payload = {
      cb1,
      cb2,
      cb3,
      session: scanSessionRef.current,
    };

    handleConfirm(payload);
  };

  const handleConfirm = async (payload = null) => {
    const codes = payload || scannedCodesRef.current;

    const { cb1, cb2, cb3 } = codes;

    if (!cb1 || !cb2 || !cb3) {
      return;
    }

    try {
      setLoading(true);

      const limpiarCodigo = (codigo) => codigo.replace(/[()-]/g, "");
      await onConfirm({
        cb1: limpiarCodigo(cb1),
        cb2: limpiarCodigo(cb2),
        cb3: limpiarCodigo(cb3),
      });

      // Nueva sesión de escaneo
      scanSessionRef.current += 1;

      scannedCodesRef.current = {
        cb1: "",
        cb2: "",
        cb3: "",
      };

      setCb1Segments(["", "", "", ""]);
      setCb2Segments([""]);
      setCb3Segments(["", ""]);
    } finally {
      setLoading(false);
      setIsAutoDetected(false);
    }
  };

  // const handleConfirm = async () => {
  //   const fullCb1 = getCleanCode(cb1Segments, "cb1");
  //   const fullCb2 = getCleanCode(cb2Segments, "cb2");
  //   const fullCb3 = getCleanCode(cb3Segments, "cb3");

  //   if (!fullCb1.trim() || !fullCb2.trim() || !fullCb3.trim()) return;

  //   try {
  //     setLoading(true);
  //     await onConfirm({ cb1: fullCb1, cb2: fullCb2, cb3: fullCb3 });
  //   } finally {
  //     setLoading(false);
  //     setIsAutoDetected(false); // Resetear flag al confirmar
  //   }
  // };

  const toggleKeypad = (fieldKey) => {
    setActiveKeypad((prev) => (prev === fieldKey ? null : fieldKey));
    setIsAutoDetected(false); // Si cambia a manual, resetear flag
  };

  const shouldShowField = (fieldKey) => {
    if (!activeKeypad) return true;
    return activeKeypad === fieldKey;
  };

  const fullCb1 = getCleanCode(cb1Segments, "cb1");
  const fullCb2 = getCleanCode(cb2Segments, "cb2");
  const fullCb3 = getCleanCode(cb3Segments, "cb3");
  const allCodesFilled = fullCb1.trim() && fullCb2.trim() && fullCb3.trim();
  const canUseMainButtons = !activeKeypad && allCodesFilled;

  return (
    <div style={{ padding: "0 8px" }}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {shouldShowField("cb1") && (
          <SegmentedInput
            label="Código de barras (vto y cantidad):"
            segments={cb1Segments}
            setSegments={setCb1Segments}
            segmentLabels={cb1Config.labels}
            segmentAIs={cb1Config.ais}
            isActive={activeKeypad === "cb1"}
            onToggleKeypad={() => toggleKeypad("cb1")}
            isAlphanumeric={segmentConfig.cb1.isAlphanumeric}
            type={segmentConfig.cb1.type}
            darkMode={darkMode}
          />
        )}

        {shouldShowField("cb2") && (
          <SegmentedInput
            label="Código de barras (lote):"
            segments={cb2Segments}
            setSegments={setCb2Segments}
            segmentLabels={cb2Config.labels}
            segmentAIs={cb2Config.ais}
            isActive={activeKeypad === "cb2"}
            onToggleKeypad={() => toggleKeypad("cb2")}
            isAlphanumeric={segmentConfig.cb2.isAlphanumeric}
            type={segmentConfig.cb2.type}
            darkMode={darkMode}
          />
        )}

        {shouldShowField("cb3") && (
          <SegmentedInput
            label="Código de barras (palet + artículo):"
            segments={cb3Segments}
            setSegments={setCb3Segments}
            segmentLabels={cb3Config.labels}
            segmentAIs={cb3Config.ais}
            isActive={activeKeypad === "cb3"}
            onToggleKeypad={() => toggleKeypad("cb3")}
            isAlphanumeric={segmentConfig.cb3.isAlphanumeric}
            type={segmentConfig.cb3.type}
            darkMode={darkMode}
          />
        )}

        <Space
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}
        >
          <Button
            onClick={onCancel}
            style={{
              backgroundColor: "#ff4d4f",
              borderColor: "#ff4d4f",
              color: "#fff",
              width: 100,
            }}
          >
            Cerrar
          </Button>
          <Button
            loading={loading}
            disabled={!canUseMainButtons || loading}
            style={{
              backgroundColor: canUseMainButtons ? "#52c41a" : "#d9d9d9",
              borderColor: canUseMainButtons ? "#52c41a" : "#d9d9d9",
              color: "#fff",
              width: 150,
              cursor: canUseMainButtons ? "pointer" : "not-allowed",
            }}
            onClick={handleConfirm}
          >
            Confirmar
          </Button>
        </Space>
      </Space>
    </div>
  );
};

export default ManualBarcodeInput;
